<script setup lang="ts">
import type { ContactTopic } from '@/model/communication/contact-topic'
import type { ContactTopicGroup } from '@/model/communication/contact-topic-group'
import { useHttpClient } from '@/vf'
import { computed, inject, ref, watch } from 'vue'
import draggable from 'vuedraggable'
import type { EditorNode, EditorOption } from './types'

const { node } = defineProps<{ node: EditorNode }>()

const http = useHttpClient()
const contactTopicGroupId = inject<string | undefined>('contactTopicGroupId', undefined)
const availableTopics = ref<ContactTopic[]>([])
const topicsLoaded = ref(false)

watch(() => contactTopicGroupId, async id => {
    if (topicsLoaded.value || !id) {
        return
    }
    topicsLoaded.value = true
    const { data } = await http.get<ContactTopicGroup>(`/contact-topic/group/${id}`)
    availableTopics.value = data.topics ?? []
}, { immediate: true })

const unselectedTopics = computed(() => {
    const selectedIds = new Set(node.options.map(o => o.value))
    return availableTopics.value.filter(topic => !selectedIds.has(topic.id ?? ''))
})

function topicLabel(opt: EditorOption): string {
    return availableTopics.value.find(topic => topic.id === opt.value)?.name ?? opt.label
}

function addTopic(topic: ContactTopic) {
    node.options.push({ label: topic.name ?? '', value: topic.id ?? '' })
}

function removeTopic(id: string) {
    node.options = node.options.filter(o => o.value !== id)
}
</script>

<template>
    <div class="fb-field">
        <label class="fb-label">{{ $t("components:form_builder.topics") }}</label>
        <div class="fb-hint">{{ $t("components:form_builder.topics_hint") }}</div>

        <div v-if="!contactTopicGroupId" class="fb-hint mt-1">
            {{ $t("components:form_builder.topics_save_group_hint") }}
        </div>
        <div v-else-if="topicsLoaded && availableTopics.length === 0" class="fb-hint mt-1">
            {{ $t("components:form_builder.topics_none_available") }}
        </div>
        <template v-else>
            <draggable v-model="node.options" item-key="value" handle=".opt-handle" class="fb-options mt-2">
                <template #item="{ element: opt }">
                    <div class="fb-option">
                        <i class="fa fa-bars opt-handle fb-option__handle"></i>
                        <label class="fb-label fb-checkbox flex-grow-1">
                            <input type="checkbox" checked @change="removeTopic(opt.value)" />
                            {{ topicLabel(opt) }}
                        </label>
                    </div>
                </template>
            </draggable>

            <div v-if="unselectedTopics.length" class="fb-options mt-2">
                <div v-for="topic in unselectedTopics" :key="topic.id" class="fb-option">
                    <label class="fb-label fb-checkbox flex-grow-1">
                        <input type="checkbox" @change="addTopic(topic)" />
                        {{ topic.name }}
                    </label>
                </div>
            </div>
        </template>
    </div>
</template>
